import { useCallback, useEffect, useState } from 'react';
import { App, Button, Drawer, Form, Input, Modal, Select, Table, Tabs, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, TeamOutlined } from '@ant-design/icons';
import {
  addProjectMember,
  closeProject,
  createProject,
  linkProjectResource,
  listProjectMembers,
  listProjectResources,
  listProjects,
  revokeProjectMember,
  unlinkProjectResource,
  type Project,
  type ProjectMember,
  type ProjectResource,
} from '@/api/teamAccess';
import { isNotAvailable, normalizeApiError } from '@/api/errors';
import { EmptyState } from '@/components/EmptyState';
import { useAuth } from '@/auth/OlAuthContext';
import { formatAbsolute } from '@/lib/time';
import { SCOPE_RESOURCE_TYPES } from './bindableKeys';

const { Paragraph, Text } = Typography;

/**
 * B1 · UC2 §4 — projects (Team & Access): an authority-and-grouping scope, NEVER an isolation
 * fence (departments are spokes). Members (users or roles) feed the ACL PROJECT grantee and the
 * effective-authority project list; closing a project deadens its grants with no sweep. The
 * console hides, the server re-checks (§12).
 */
export function ProjectsPanel() {
  const { message } = App.useApp();
  const { user } = useAuth();
  const canWrite = user?.roles?.includes('hub_admin') ?? false;
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm<{ projectKey: string; name: string }>();
  const [memberProject, setMemberProject] = useState<Project | null>(null);
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [memberForm] = Form.useForm<{ memberType: 'USER' | 'ROLE'; member: string; projectRole: string }>();
  const [resources, setResources] = useState<ProjectResource[]>([]);
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [resourceForm] = Form.useForm<{ resourceType: string; resourceId: string }>();

  const load = useCallback(async () => {
    setLoading(true);
    setUnavailable(false);
    try {
      setProjects(await listProjects());
    } catch (e) {
      const n = normalizeApiError(e);
      if (isNotAvailable(n)) setUnavailable(true);
      else message.error(n.message);
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadResources = async (project: Project) => {
    setResourcesLoading(true);
    try {
      setResources(await listProjectResources(project.projectId));
    } catch (e) {
      message.error(normalizeApiError(e).message);
    } finally {
      setResourcesLoading(false);
    }
  };

  const openMembers = async (project: Project) => {
    setMemberProject(project);
    setMembersLoading(true);
    void loadResources(project); // both drawer tabs load together — a tab switch shows real state
    try {
      setMembers(await listProjectMembers(project.projectId));
    } catch (e) {
      message.error(normalizeApiError(e).message);
    } finally {
      setMembersLoading(false);
    }
  };

  const columns: ColumnsType<Project> = [
    { title: 'Key', dataIndex: 'projectKey', render: (v: string) => <Text code>{v}</Text> },
    { title: 'Name', dataIndex: 'name' },
    {
      title: 'Scope',
      dataIndex: 'spokeIdPin',
      width: 130,
      render: (pin: string | null) => (pin ? <Tag>{`spoke ${pin}`}</Tag> : <Tag color="blue">hub-wide</Tag>),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 100,
      render: (s: string) => <Tag color={s === 'ACTIVE' ? 'green' : 'default'}>{s}</Tag>,
    },
    { title: 'Created', dataIndex: 'createdAt', width: 170, render: (v) => formatAbsolute(v) },
    {
      title: '',
      key: 'actions',
      width: 170,
      render: (_: unknown, p: Project) => (
        <span style={{ display: 'inline-flex', gap: 8 }}>
          <Button size="small" icon={<TeamOutlined />} onClick={() => void openMembers(p)}>
            Members
          </Button>
          {canWrite && p.status === 'ACTIVE' && (
            <Button size="small" danger onClick={async () => {
              try {
                await closeProject(p.projectId);
                message.success(`${p.projectKey} closed — its grants match nobody from the next decision`);
                void load();
              } catch (e) {
                message.error(normalizeApiError(e).message);
              }
            }}>
              Close
            </Button>
          )}
        </span>
      ),
    },
  ];

  if (unavailable) {
    return <EmptyState title="Projects are not available" description="The IAM UC2 surface is not enabled in this deployment." />;
  }

  return (
    <div>
      <Paragraph type="secondary" style={{ marginBottom: 12, maxWidth: 780, fontSize: 13 }}>
        Projects group people and work for AUTHORITY (ACL project grants, delegation scopes) — they are
        never an isolation fence; departments are spokes. A spoke-bound author&apos;s project pins to
        their spoke; closing a project deadens its grants immediately.
      </Paragraph>
      {canWrite && (
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)} style={{ marginBottom: 12 }}>
          New project
        </Button>
      )}
      <Table rowKey="projectId" size="small" columns={columns} dataSource={projects} loading={loading}
             pagination={{ pageSize: 20, hideOnSinglePage: true }} />

      <Modal title="New project" open={modalOpen} onCancel={() => setModalOpen(false)}
             onOk={() => form.submit()} confirmLoading={submitting} okText="Create">
        <Form form={form} layout="vertical" onFinish={async (v) => {
          setSubmitting(true);
          try {
            await createProject({ projectKey: v.projectKey.trim(), name: v.name.trim() });
            message.success(`Project ${v.projectKey.trim()} created`);
            setModalOpen(false);
            form.resetFields();
            void load();
          } catch (e) {
            message.error(normalizeApiError(e).message);
          } finally {
            setSubmitting(false);
          }
        }}>
          <Form.Item name="projectKey" label="Project key" rules={[{ required: true }]}>
            <Input placeholder="apac-onboarding" />
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input placeholder="APAC onboarding team" />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer title={memberProject ? `Project — ${memberProject.projectKey}` : 'Project'}
              open={memberProject !== null} onClose={() => setMemberProject(null)} width={560}>
        <Tabs
          defaultActiveKey="members"
          size="small"
          items={[
            { key: 'members', label: 'Members', children: renderMembers() },
            { key: 'resources', label: 'Resources', children: renderResources() },
          ]}
        />
      </Drawer>
    </div>
  );

  function renderMembers() {
    return (
      <div>
        {canWrite && memberProject?.status === 'ACTIVE' && (
          <Form form={memberForm} layout="inline" style={{ marginBottom: 12, rowGap: 8 }}
                initialValues={{ memberType: 'USER', projectRole: 'MEMBER' }}
                onFinish={async (v) => {
                  if (!memberProject) return;
                  try {
                    await addProjectMember(memberProject.projectId, {
                      memberType: v.memberType, member: v.member.trim(), projectRole: v.projectRole,
                    });
                    memberForm.resetFields(['member']);
                    void openMembers(memberProject);
                  } catch (e) {
                    message.error(normalizeApiError(e).message);
                  }
                }}>
            <Form.Item name="memberType" noStyle>
              <Select style={{ width: 90 }} options={[{ value: 'USER' }, { value: 'ROLE' }]} />
            </Form.Item>
            <Form.Item name="member" noStyle rules={[{ required: true }]}>
              <Input style={{ width: 180 }} placeholder="user sub | role name" />
            </Form.Item>
            <Form.Item name="projectRole" noStyle>
              <Select style={{ width: 110 }}
                      options={[{ value: 'LEAD' }, { value: 'MEMBER' }, { value: 'VIEWER' }]} />
            </Form.Item>
            <Button htmlType="submit" type="primary" size="middle">Add</Button>
          </Form>
        )}
        <Table rowKey={(m) => `${m.memberType}:${m.member}:${m.addedAt}`} size="small"
               loading={membersLoading} dataSource={members} pagination={false}
               columns={[
                 { title: 'Type', dataIndex: 'memberType', width: 70 },
                 { title: 'Member', dataIndex: 'member', render: (v: string) => <Text code>{v}</Text> },
                 { title: 'Role', dataIndex: 'projectRole', width: 90 },
                 {
                   title: 'Status', key: 'status', width: 90,
                   render: (_: unknown, m: ProjectMember) =>
                     m.revokedAt ? <Tag>revoked</Tag> : <Tag color="green">live</Tag>,
                 },
                 ...(canWrite
                   ? [{
                       title: '', key: 'a', width: 90,
                       render: (_: unknown, m: ProjectMember) =>
                         !m.revokedAt && memberProject ? (
                           <Button size="small" danger onClick={async () => {
                             try {
                               await revokeProjectMember(memberProject.projectId, m.memberType, m.member);
                               void openMembers(memberProject);
                             } catch (e) {
                               message.error(normalizeApiError(e).message);
                             }
                           }}>
                             Revoke
                           </Button>
                         ) : null,
                     }]
                   : []),
               ]} />
      </div>
    );
  }

  /**
   * §4 resources — the grouping half of a project: linking a work item / flow / entity /
   * dataroom invite makes PROJECT-grantee ACL rules reach it. Unlink is an UPDATE row-flip
   * (I7): the row stays with unlink provenance, so history reads honestly.
   */
  function renderResources() {
    const live = resources.filter((r) => !r.unlinkedAt);
    return (
      <div>
        <Paragraph type="secondary" style={{ fontSize: 12.5, marginBottom: 12 }}>
          Resources linked to this project. A PROJECT-grantee ACL rule reaches exactly the live links
          below; unlinking keeps the row with its provenance (nothing is deleted).
        </Paragraph>
        {canWrite && memberProject?.status === 'ACTIVE' && (
          <Form form={resourceForm} layout="inline" style={{ marginBottom: 12, rowGap: 8 }}
                initialValues={{ resourceType: 'work_item' }}
                onFinish={async (v) => {
                  if (!memberProject) return;
                  try {
                    await linkProjectResource(memberProject.projectId, {
                      resourceType: v.resourceType, resourceId: v.resourceId.trim(),
                    });
                    resourceForm.resetFields(['resourceId']);
                    void loadResources(memberProject);
                  } catch (e) {
                    message.error(normalizeApiError(e).message);
                  }
                }}>
            <Form.Item name="resourceType" noStyle>
              <Select style={{ width: 150 }}
                      options={SCOPE_RESOURCE_TYPES.map((t) => ({ value: t }))} />
            </Form.Item>
            <Form.Item name="resourceId" noStyle rules={[{ required: true }]}>
              <Input style={{ width: 200 }} placeholder="resource id (ULID)" />
            </Form.Item>
            <Button htmlType="submit" type="primary" size="middle">Link</Button>
          </Form>
        )}
        {!resourcesLoading && live.length === 0 && resources.length === 0 ? (
          <Paragraph type="secondary" style={{ fontSize: 12.5 }}>
            Nothing is linked yet. Link a case, flow, entity or data-room invite to put it in reach
            of this project&apos;s grants.
          </Paragraph>
        ) : (
          <Table rowKey={(r) => `${r.resourceType}:${r.resourceId}:${r.linkedAt}`} size="small"
                 loading={resourcesLoading} dataSource={resources} pagination={false}
                 columns={[
                   { title: 'Type', dataIndex: 'resourceType', width: 120, render: (v: string) => <Tag>{v}</Tag> },
                   { title: 'Resource', dataIndex: 'resourceId', render: (v: string) => <Text code>{v}</Text> },
                   {
                     title: 'Status', key: 'status', width: 90,
                     render: (_: unknown, r: ProjectResource) =>
                       r.unlinkedAt ? <Tag>unlinked</Tag> : <Tag color="green">live</Tag>,
                   },
                   ...(canWrite
                     ? [{
                         title: '', key: 'a', width: 90,
                         render: (_: unknown, r: ProjectResource) =>
                           !r.unlinkedAt && memberProject ? (
                             <Button size="small" danger onClick={async () => {
                               try {
                                 await unlinkProjectResource(memberProject.projectId, r.resourceType, r.resourceId);
                                 void loadResources(memberProject);
                               } catch (e) {
                                 message.error(normalizeApiError(e).message);
                               }
                             }}>
                               Unlink
                             </Button>
                           ) : null,
                       }]
                     : []),
                 ]} />
        )}
      </div>
    );
  }
}
